import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Select from '@/components/@IntranetLibrary/Select';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildMarketingCaseContentHtml,
  MarketingCaseSnapshot,
} from '@/components/Approval/@components/MarketingCase';
import { getMarketingCaseDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildMarketingCaseCandidateLabel,
  MARKETING_CASE_EMPTY_MESSAGE,
  marketingCaseCandidateKey,
  type MarketingCaseDraftSource,
} from '@/utils/approval/marketingCase';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = { isEditMode: boolean };

/**
 * (구)마케팅 건별 진행(83) — 진행 대기 건 선택 → 「확인」으로 표 확정, 제목은 「확인」마다 진행업무명으로 덮어씀.
 * 셀렉트만 바꾸면 확정한 표·제목은 그대로(레거시). 편집은 건을 바꿀 수 없고 저장된 표만
 */
const MarketingCaseField = ({ isEditMode }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const confirmed = useWatch({ control, name: 'marketingCase' });

  const { data: source, isLoading } = useSWR<MarketingCaseDraftSource>(
    isEditMode ? null : getMarketingCaseDraftSourceUrl(),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = source?.display.candidates ?? [];

  const [selectedKey, setSelectedKey] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  // 확인 중 다른 건을 고르고 다시 확인하면 늦게 온 이전 응답이 표를 덮지 않게
  const requestIdRef = useRef(0);

  // 레거시: 목록을 받으면 첫 건 선택
  useEffect(() => {
    if (selectedKey || !candidates.length) return;
    setSelectedKey(marketingCaseCandidateKey(candidates[0]));
  }, [candidates, selectedKey]);

  const handleConfirm = async () => {
    if (isConfirming) return;
    const option = candidates.find(
      (c) => marketingCaseCandidateKey(c) === selectedKey,
    );
    if (!option) {
      snackbar.showError({ message: t(MARKETING_CASE_EMPTY_MESSAGE) });
      return;
    }
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsConfirming(true);
    try {
      const key = {
        brandCode: option.brandCode,
        season: option.season,
        costDocId: option.costDocId,
        approvalDate: option.approvalDate,
      };
      const data = await getNestedDataFetcher<MarketingCaseDraftSource>(
        getMarketingCaseDraftSourceUrl(key),
      );
      if (requestId !== requestIdRef.current) return;
      const { display } = data;
      if (!display.header) throw new Error('header missing');
      setValue('subject', display.subject ?? display.header.title ?? '', {
        shouldDirty: true,
      });
      setValue(
        'marketingCase',
        {
          ...(display.selection ?? key),
          content: buildMarketingCaseContentHtml(
            display,
            option.brandName ?? '',
            t,
          ),
          linkPending: true,
        },
        { shouldDirty: true },
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setIsConfirming(false);
    }
  };

  const table = confirmed?.content ? (
    <MarketingCaseSnapshot content={confirmed.content} />
  ) : null;

  if (isEditMode) return <FieldBlock>{table}</FieldBlock>;

  return (
    <FieldBlock>
      <FieldHeader>
        <Body1>{t('마케팅 품의 진행')}</Body1>
      </FieldHeader>
      <ControlRow>
        <Select
          value={selectedKey}
          options={candidates.map((c) => ({
            value: marketingCaseCandidateKey(c),
            label: buildMarketingCaseCandidateLabel(c),
          }))}
          placeholder={
            isLoading ? t('불러오는 중...') : t(MARKETING_CASE_EMPTY_MESSAGE)
          }
          onChange={(e) => setSelectedKey(String(e.target.value))}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        <BoxButton
          type="button"
          variant="keyColor"
          size="medium"
          disabled={isLoading || isConfirming}
          startIcon={
            isConfirming ? <Spinner theme="white" size="16px" /> : undefined
          }
          onClick={handleConfirm}
        >
          {t('확인')}
        </BoxButton>
      </ControlRow>
      {table}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

export default MarketingCaseField;
